import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Phone, Mail, MapPin, MessageSquare, Clock, Calendar, ExternalLink, ChevronDown, Save, UserPlus } from 'lucide-react';
import apiService from './services/api';
import { Panel, Section, Badge, LoadingSpinner, CallModal, SMSModal } from './components';

function LeadDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [person, setPerson] = useState(null);
  const [clientId, setClientId] = useState(null);
  const [interactions, setInteractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [leadStatus, setLeadStatus] = useState('');
  const [lostReason, setLostReason] = useState('');
  const [snoozeDate, setSnoozeDate] = useState('');
  const [leadNote, setLeadNote] = useState('');
  const [savingStatus, setSavingStatus] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [users, setUsers] = useState([]);
  const [assignedTo, setAssignedTo] = useState(null);
  const [callTarget, setCallTarget] = useState(null);
  const [smsTarget, setSmsTarget] = useState(null);
  const [currentUserName, setCurrentUserName] = useState(null);

  useEffect(() => {
    fetchData();
    apiService.get('/amn/leads/assignable-users/').then(setUsers).catch(() => {});
    apiService.get('/auth/profile/').then(d => { if (d?.first_name) setCurrentUserName(d.first_name); }).catch(() => {});
  }, [id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [personData, interactionsData, clientData] = await Promise.all([
        apiService.get(`/amn/persons/${id}/`),
        apiService.getInteractions({ person: id, ordering: '-date_time', page_size: 50 }),
        apiService.getClients({ person: id, page_size: 1 }).catch(() => null),
      ]);
      setPerson(personData);
      setLeadStatus(personData.lead_status || '');
      setLostReason(personData.lead_lost_reason || '');
      setLeadNote(personData.lead_note || '');
      setSnoozeDate(personData.lead_snooze_until || '');
      setInteractions(interactionsData.results || interactionsData || []);
      const clients = clientData?.results || clientData || [];
      if (clients.length > 0) setClientId(clients[0].id);
      // Fetch current assignment
      try {
        const leadsData = await apiService.get('/amn/metrics/leads-by-person/', { search: personData.phone_number || personData.first_name, page_size: 1 });
        const lead = (leadsData.leads || []).find(l => l.person_id === parseInt(id));
        if (lead?.assigned_to) setAssignedTo(lead.assigned_to);
      } catch {}
    } catch (err) {
      console.error('Failed to load lead:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (newStatus === leadStatus) return;
    if (newStatus === 'snoozed') return; // handled by snooze button
    setSavingStatus(true);
    try {
      await apiService.updateLeadStatus(parseInt(id), newStatus, newStatus === 'lost' ? lostReason : '');
      setLeadStatus(newStatus);
      if (newStatus !== 'lost') setLostReason('');
      if (newStatus !== 'snoozed') setSnoozeDate('');
      setPerson(prev => ({ ...prev, lead_status: newStatus }));
    } catch (err) {
      console.error('Failed to update status:', err);
    } finally {
      setSavingStatus(false);
    }
  };

  const handleSnooze = async () => {
    if (!snoozeDate) return;
    setSavingStatus(true);
    try {
      await apiService.updateLeadStatus(parseInt(id), 'snoozed', '', snoozeDate);
      setLeadStatus('follow_up');
      setPerson(prev => ({ ...prev, lead_status: 'follow_up', lead_snooze_until: snoozeDate }));
    } catch (err) {
      console.error('Failed to snooze:', err);
    } finally {
      setSavingStatus(false);
    }
  };

  const handleSaveNote = async () => {
    setSavingNote(true);
    try {
      await apiService.updateLeadNote(parseInt(id), leadNote);
      setPerson(prev => ({ ...prev, lead_note: leadNote }));
    } catch (err) {
      console.error('Failed to save note:', err);
    } finally {
      setSavingNote(false);
    }
  };

  const handleAssign = async (userId) => {
    try {
      const result = await apiService.assignLead(parseInt(id), userId);
      setAssignedTo(result.assigned_to);
    } catch (err) {
      console.error('Failed to assign:', err);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  const getTypeIcon = (type) => {
    if (type?.includes('call') || type?.includes('phone')) return '📞';
    if (type?.includes('sms') || type?.includes('text')) return '💬';
    if (type?.includes('website') || type?.includes('lead')) return '🌐';
    if (type?.includes('email')) return '📧';
    return '📋';
  };

  const getDirectionBadge = (interaction) => {
    const meta = interaction.metadata || {};
    const dir = meta.direction;
    if (dir === 'incoming') return <Badge variant="info" small>Incoming</Badge>;
    if (dir === 'outgoing') return <Badge variant="neutral" small>Outgoing</Badge>;
    if (interaction.type?.includes('website')) return <Badge variant="warning" small>Website</Badge>;
    return null;
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <LoadingSpinner />
      </div>
    );
  }

  if (!person) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <p className="text-gray-500">Contact not found</p>
      </div>
    );
  }

  const fullName = `${person.first_name || ''} ${person.last_name || ''}`.trim() || 'Unknown';
  const hasClient = person.client_profile || person.client_id;

  // Separate by type
  const inbound = interactions.filter(i => {
    const meta = i.metadata || {};
    return meta.direction === 'incoming' || i.type?.includes('website') || i.type?.includes('lead') || i.type?.includes('call') && !i.type?.includes('outgoing');
  });
  const outbound = interactions.filter(i => {
    const meta = i.metadata || {};
    return meta.direction === 'outgoing' || i.type?.includes('outgoing');
  });

  const firstContact = interactions.length > 0 ? interactions[interactions.length - 1] : null;
  const lastContact = interactions.length > 0 ? interactions[0] : null;

  return (
    <div className="max-w-4xl mx-auto p-4">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <button onClick={() => navigate(-1)} className="p-1 hover:bg-gray-100 rounded">
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-semibold text-gray-900">{fullName}</h1>
          <div className="flex items-center gap-2 mt-0.5">
            {clientId ? (
              <>
                <Badge variant="success" small>Client</Badge>
                <button
                  onClick={() => navigate(`/clients/${clientId}`)}
                  className="text-xs text-red-700 hover:text-red-900 hover:underline flex items-center gap-0.5"
                >
                  View Client Page <ExternalLink className="w-3 h-3" />
                </button>
              </>
            ) : (
              <Badge variant="warning" small>Lead / Contact</Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {person.phone_number && (
            <>
              <button
                onClick={() => setCallTarget({ phone: person.phone_number, name: fullName })}
                className="px-3 py-1.5 text-xs bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-1"
              >
                <Phone className="w-3.5 h-3.5" />
                Call
              </button>
              <button
                onClick={() => setSmsTarget({ phone: person.phone_number, name: fullName, personId: parseInt(id) })}
                className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1"
              >
                <MessageSquare className="w-3.5 h-3.5" />
                Text
              </button>
            </>
          )}
        </div>
      </div>

      {/* Contact Info */}
      <Panel className="mb-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-1">
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-[10px] text-gray-500 uppercase">Phone</p>
              {person.phone_number ? (
                <a href={`tel:${person.phone_number}`} className="text-sm text-red-700 hover:underline">{person.phone_number}</a>
              ) : (
                <p className="text-sm text-gray-400">N/A</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-[10px] text-gray-500 uppercase">Email</p>
              {person.email ? (
                <a href={`mailto:${person.email}`} className="text-sm text-red-700 hover:underline">{person.email}</a>
              ) : (
                <p className="text-sm text-gray-400">N/A</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-[10px] text-gray-500 uppercase">Address</p>
              <p className="text-sm text-gray-900">
                {[person.address, person.city, person.zip_code].filter(Boolean).join(', ') || 'N/A'}
              </p>
            </div>
          </div>
        </div>
      </Panel>

      {/* Lead Management */}
      <Panel className="mb-3 !p-3">
        <div className="space-y-2.5">
          {/* Status buttons */}
          <div>
            <p className="text-[10px] text-gray-400 font-medium uppercase mb-1">Status</p>
            <div className="flex flex-wrap gap-1">
              {[
                { v: 'new', l: 'New', active: 'bg-blue-600 text-white', inactive: 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200' },
                { v: 'follow_up', l: 'Follow Up', active: 'bg-amber-600 text-white', inactive: 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200' },
                { v: 'booked', l: 'Booked', active: 'bg-green-600 text-white', inactive: 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200' },
                { v: 'lost', l: 'Lost', active: 'bg-red-600 text-white', inactive: 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200' },
                { v: 'not_a_lead', l: 'Not a Lead', active: 'bg-gray-600 text-white', inactive: 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200' },
                { v: 'archived', l: 'Archived', active: 'bg-gray-500 text-white', inactive: 'bg-gray-50 text-gray-500 hover:bg-gray-100 border border-gray-200' },
              ].map(s => (
                <button key={s.v} onClick={() => handleStatusChange(s.v)} disabled={savingStatus}
                  className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${
                    leadStatus === s.v ? s.active : s.inactive
                  } disabled:opacity-50`}>
                  {s.l}
                </button>
              ))}
            </div>
          </div>

          {/* Lost reason */}
          {leadStatus === 'lost' && (
            <div>
              <p className="text-[10px] text-gray-400 font-medium uppercase mb-1">Lost Reason</p>
              <input
                type="text"
                value={lostReason}
                onChange={(e) => setLostReason(e.target.value)}
                onBlur={() => { if (lostReason !== person.lead_lost_reason) apiService.updateLeadStatus(parseInt(id), 'lost', lostReason); }}
                placeholder="Why was this lead lost?"
                className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800 focus:outline-none"
              />
            </div>
          )}

          {/* Snooze */}
          <div>
            <p className="text-[10px] text-gray-400 font-medium uppercase mb-1">
              Snooze {snoozeDate && new Date(snoozeDate + 'T12:00:00') > new Date() && <span className="text-purple-600 normal-case">(until {new Date(snoozeDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})</span>}
            </p>
            <div className="flex items-center gap-1.5">
              <input
                type="date"
                value={snoozeDate}
                onChange={(e) => setSnoozeDate(e.target.value)}
                min={new Date().toISOString().slice(0, 10)}
                className="px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-purple-600 focus:outline-none"
              />
              <button onClick={handleSnooze} disabled={!snoozeDate || savingStatus}
                className="px-2.5 py-1 text-xs font-medium rounded bg-purple-100 text-purple-700 hover:bg-purple-200 disabled:opacity-50">
                Snooze
              </button>
              {snoozeDate && (
                <button onClick={async () => { await apiService.updateLeadStatus(parseInt(id), 'follow_up'); setSnoozeDate(''); setLeadStatus('follow_up'); }}
                  className="px-2.5 py-1 text-xs font-medium rounded bg-gray-100 text-gray-600 hover:bg-gray-200">
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Assignment */}
          <div>
            <p className="text-[10px] text-gray-400 font-medium uppercase mb-1">Assigned To</p>
            <div className="flex flex-wrap gap-1">
              <button onClick={() => handleAssign(null)}
                className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${
                  !assignedTo ? 'bg-gray-600 text-white' : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
                }`}>
                Unassigned
              </button>
              {users.map(u => (
                <button key={u.id} onClick={() => handleAssign(u.id)}
                  className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${
                    assignedTo?.user_id === u.id
                      ? 'bg-red-800 text-white'
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
                  }`}>
                  {u.first_name}
                </button>
              ))}
            </div>
          </div>

          {/* Note */}
          <div>
            <p className="text-[10px] text-gray-400 font-medium uppercase mb-1">Lead Note</p>
            <div className="flex gap-1.5">
              <input
                type="text"
                value={leadNote}
                onChange={(e) => setLeadNote(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSaveNote(); }}
                placeholder="Add a note about this lead..."
                className="flex-1 px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800 focus:outline-none"
              />
              <button onClick={handleSaveNote} disabled={savingNote}
                className="px-2.5 py-1 text-xs font-medium rounded bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 flex items-center gap-1">
                <Save className="w-3 h-3" /> Save
              </button>
            </div>
          </div>
        </div>
      </Panel>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        <Panel className="!p-2 text-center">
          <div className="text-lg font-bold text-gray-900">{interactions.length}</div>
          <div className="text-[10px] text-gray-500 uppercase">Total Interactions</div>
        </Panel>
        <Panel className="!p-2 text-center">
          <div className="text-lg font-bold text-blue-600">{inbound.length}</div>
          <div className="text-[10px] text-gray-500 uppercase">Inbound</div>
        </Panel>
        <Panel className="!p-2 text-center">
          <div className="text-lg font-bold text-green-600">{outbound.length}</div>
          <div className="text-[10px] text-gray-500 uppercase">Outbound</div>
        </Panel>
        <Panel className="!p-2 text-center">
          <div className="text-lg font-bold text-gray-600">
            {firstContact ? new Date(firstContact.date_time).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
          </div>
          <div className="text-[10px] text-gray-500 uppercase">First Contact</div>
        </Panel>
      </div>

      {/* Timeline */}
      <Panel>
        <Section title={`Interaction Timeline (${interactions.length})`}>
          {interactions.length === 0 ? (
            <p className="text-xs text-gray-500 text-center py-4">No interactions found</p>
          ) : (
            <div className="space-y-1">
              {interactions.map((interaction) => {
                const meta = interaction.metadata || {};
                const isAuto = !!meta.automated;
                const source = meta.source || '';

                return (
                  <div key={interaction.id} className={`rounded-lg border px-3 py-2 ${
                    meta.direction === 'incoming' ? 'bg-blue-50 border-blue-200' :
                    meta.direction === 'outgoing' ? 'bg-green-50 border-green-200' :
                    interaction.type?.includes('website') ? 'bg-yellow-50 border-yellow-200' :
                    'bg-white border-gray-200'
                  }`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm">{getTypeIcon(interaction.type)}</span>
                        <span className="text-xs font-medium text-gray-700 capitalize">
                          {interaction.type?.replace(/_/g, ' ') || 'Unknown'}
                        </span>
                        {getDirectionBadge(interaction)}
                        {isAuto && <Badge variant="neutral" small>Auto</Badge>}
                        {source && <span className="text-[9px] text-gray-400">{source}</span>}
                      </div>
                      <div className="flex items-center gap-1 text-[10px] text-gray-400">
                        <Clock className="w-3 h-3" />
                        {formatDate(interaction.date_time)}
                      </div>
                    </div>
                    {interaction.content && (() => {
                      const isLong = interaction.content.length > 200;
                      const isExp = expanded[interaction.id];
                      return (
                        <>
                          <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-line">
                            {isExp || !isLong ? interaction.content : interaction.content.slice(0, 200) + '...'}
                          </p>
                          {isLong && (
                            <button
                              onClick={() => setExpanded(prev => ({ ...prev, [interaction.id]: !prev[interaction.id] }))}
                              className="text-[10px] text-blue-600 hover:text-blue-800 font-medium mt-1"
                            >
                              {isExp ? 'Show less' : 'Show more'}
                            </button>
                          )}
                        </>
                      );
                    })()}
                  </div>
                );
              })}
            </div>
          )}
        </Section>
      </Panel>

      {callTarget && (
        <CallModal
          target={callTarget}
          onClose={() => setCallTarget(null)}
          apiService={apiService}
        />
      )}
      {smsTarget && (
        <SMSModal
          target={smsTarget}
          onClose={() => setSmsTarget(null)}
          apiService={apiService}
          senderName={currentUserName}
        />
      )}
    </div>
  );
}

export default LeadDetailPage;
